import {
  DateCell,
  DateFormats,
  formatDateWithTimeAndTimezone,
  Icon,
  Inline,
  Stack,
  Tag,
  type TagIntent,
  Tooltip,
  Typography,
} from '@omnipresentgroup/design-system';
import type {
  TimesheetArbeitszeit,
  TimesheetArbeitszeitDay,
  TimesheetArbeitszeitDayTypeEnum,
  TimesheetArbeitszeitTimeSlot,
} from '@omnipresentgroup/types';
import {
  type CellContext,
  type ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { Duration } from 'luxon';
import { memo, useMemo } from 'react';

import { EMPTY_ARRAY } from '../../../constants';
import { TimesheetTimeSlotsFields } from '../components';
import { TIMESLOT_TYPES_CONFIG } from '../constants';

export enum ColumnHeaders {
  date = 'Date',
  timeSlots = 'Working hours',
  summary = 'Summary',
}

export const useTimesheetDetailsTableConfig = ({
  timesheet,
  columnsOverride,
  showFields,
}: {
  timesheet?: TimesheetArbeitszeit;
  columnsOverride?: ColumnDef<TimesheetArbeitszeitDay, unknown>[];
  showFields?: boolean;
}) => {
  const TypeCell = (info: CellContext<TimesheetArbeitszeitDay, string>) => {
    const dayType = info?.getValue() as TimesheetArbeitszeitDayTypeEnum;

    const intentPerType: Record<TimesheetArbeitszeitDayTypeEnum, TagIntent> = {
      workday: 'default',
      public_holiday: 'success',
      weekend: 'default',
    };

    const labelPerType: Record<TimesheetArbeitszeitDayTypeEnum, string> = {
      workday: 'Work day',
      public_holiday: 'Public holiday',
      weekend: 'Weekend',
    };

    return dayType === 'workday' ? (
      ''
    ) : (
      <Tag id="day-type" intent={intentPerType[dayType]}>
        {labelPerType[dayType]}
      </Tag>
    );
  };

  const TimeSlotsFieldsCell = memo(
    (
      info: CellContext<
        TimesheetArbeitszeitDay,
        TimesheetArbeitszeitTimeSlot[]
      >,
    ) =>
      info?.getValue() ? (
        <TimesheetTimeSlotsFields
          dayIndex={
            timesheet?.days.findIndex(
              (day) => day.date === info.row.original.date,
            ) || 0
          }
          timeSlotDay={info.row.original.date}
        />
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <></>
      ),
  );

  const TimeSlotsDisplayCell = memo(
    (
      info: CellContext<
        TimesheetArbeitszeitDay,
        TimesheetArbeitszeitTimeSlot[]
      >,
    ) => {
      return (
        <Stack>
          {info.getValue()?.map((slot, index) => {
            const typeConfig = TIMESLOT_TYPES_CONFIG.get(slot.type);
            return (
              <Inline key={`timeslot-${index}`} css={{ whiteSpace: 'nowrap' }}>
                <Inline gap="4">
                  <Inline w={40} justify="center">
                    <Typography as={'span'} size={'14'}>
                      {formatDateWithTimeAndTimezone(
                        slot.startTime,
                        DateFormats.Time24h,
                        { includeTimezone: false },
                      )}
                    </Typography>
                  </Inline>
                  -
                  <Inline w={40} justify="center">
                    <Typography as={'span'} size={'14'}>
                      {formatDateWithTimeAndTimezone(
                        slot.endTime,
                        DateFormats.Time24h,
                        { includeTimezone: false },
                      )}
                    </Typography>
                  </Inline>
                </Inline>
                {typeConfig && (
                  <Inline gap="8" align="center">
                    <Icon icon={typeConfig.icon} size={'16'} />
                    <Typography as={'span'} size={'14'}>
                      {typeConfig.label}
                    </Typography>
                  </Inline>
                )}
              </Inline>
            );
          })}
        </Stack>
      );
    },
  );

  const SummaryCell = memo(
    (
      info: CellContext<
        TimesheetArbeitszeitDay,
        TimesheetArbeitszeitDay['summary']
      >,
    ) => {
      return (
        <Stack gap="8">
          {Object.entries(info.getValue() || EMPTY_ARRAY)?.map(
            ([key, value]) => {
              const typeConfig = TIMESLOT_TYPES_CONFIG.get(
                key as TimesheetArbeitszeitTimeSlot['type'],
              );
              return (
                <Inline
                  key={key}
                  gap="8"
                  align="center"
                  justify="space-between"
                  css={{ whiteSpace: 'nowrap' }}
                >
                  <Typography
                    as={'span'}
                    size={'14'}
                    weight="medium"
                    color="subtle"
                  >
                    {typeConfig?.label}
                  </Typography>

                  <Inline align="center" gap="4">
                    {info.row.original.type !== 'workday' && (
                      <Tooltip
                        description="Note: Clients may need to justify to local authorities the work done on public holidays or weekends."
                        id="non-workday-warning"
                      >
                        <Icon icon="AlertCircle" size="16" color="warning" />
                      </Tooltip>
                    )}
                    <Typography as={'span'} size={'14'} weight="medium">
                      {Duration.fromISO(value)
                        .shiftTo('hours')
                        .toHuman({ unitDisplay: 'short' })}
                    </Typography>
                  </Inline>
                </Inline>
              );
            },
          )}
        </Stack>
      );
    },
  );

  const columnHelper = createColumnHelper<TimesheetArbeitszeitDay>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<TimesheetArbeitszeitDay, any>[] = useMemo(
    () => [
      columnHelper.accessor('date', {
        cell: (info) => <DateCell value={info.getValue()} />,
        header: () => ColumnHeaders.date,
        size: 165,
      }),
      columnHelper.accessor('type', {
        cell: TypeCell,
        header: () => '',
        size: 100,
      }),
      columnHelper.accessor('timeSlots', {
        cell: showFields ? TimeSlotsFieldsCell : TimeSlotsDisplayCell,
        header: () => ColumnHeaders.timeSlots,
        size: 500,
      }),
      columnHelper.accessor('summary', {
        cell: SummaryCell,
        header: () => ColumnHeaders.summary,
        size: 200,
      }),
      ...(columnsOverride ?? EMPTY_ARRAY),
    ],
    [
      SummaryCell,
      TimeSlotsDisplayCell,
      TimeSlotsFieldsCell,
      columnHelper,
      columnsOverride,
      showFields,
    ],
  );

  return {
    columns,
    columnHelper,
  };
};
